import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import useFileUpload from '../hooks/useFileUpload';

/**
 * Reusable File Upload Component for S3
 * @param {object} props - Component props
 * @param {string} props.fieldName - Unique field name for this upload
 * @param {string} props.label - Label for the upload field
 * @param {string} props.orgId - Organization ID
 * @param {string} props.section - Section name for S3 folder organization
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.accept - File types to accept
 * @param {number} props.maxSize - Max file size in MB
 * @param {boolean} props.multiple - Allow multiple files
 * @param {function} props.onChange - Callback when file is uploaded/removed
 * @param {object} props.initialFile - Initial file data if editing
 * @param {boolean} props.localDeleteOnly - If true, delete only clears state without API calls
 * @param {boolean} props.disabled - If true, disables upload and delete functionality
 * @returns {JSX.Element} File upload component
 */
const FileUploadComponent = ({
  fieldName,
  label,
  orgId,
  section = 'documents',
  required = false,
  accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx',
  maxSize = 250, // MB
  multiple = false,
  onChange = null,
  initialFile = null,
  localDeleteOnly = false,
  disabled = false
}) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  
  const {
    uploadFile,
    uploadMultipleFiles,
    deleteFile,
    removeFileReference,
    removeFileReferenceLocal,
    getFileUrl,
    getFileUrlByKey,
    getUploadInfo
  } = useFileUpload(orgId, section);

  const uploadInfo = getUploadInfo(fieldName);
  const { file, progress, error, isUploading } = uploadInfo;

  // Use initialFile if no active upload and no uploaded file
  const displayFile = file || initialFile;

  // Handle file selection
  const handleFileSelect = async (selectedFiles) => {
    const files = Array.from(selectedFiles);
    
    // Validate file size
    const oversizedFiles = files.filter(f => f.size > maxSize * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`Files too large. Maximum size is ${maxSize}MB.`);
      return;
    }

    try {
      let result;
      if (multiple) {
        result = await uploadMultipleFiles(files, fieldName);
      } else {
        result = await uploadFile(files[0], fieldName);
      }

      if (result.success && onChange) {
        onChange(fieldName, result.data);
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  // Handle file input change
  const handleInputChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset the file input value to allow re-uploading the same file
    event.target.value = '';
  };

  // Handle drag and drop
  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  // Extract original filename from S3 key or file data
  const getDisplayFileName = (fileData) => {
    if (!fileData) return 'Unknown file';
    
    // If we have originalName, use it
    if (fileData.originalName) {
      return fileData.originalName;
    }
    
    // If we have fileName and it's not an S3 key format, use it
    if (fileData.fileName && !fileData.fileName.includes('/')) {
      return fileData.fileName;
    }
    
    // Extract filename from S3 key format like "applications/67474/organization/1757496938579_a3f81a8b7e661b46_sample.pdf"
    if (fileData.key) {
      const parts = fileData.key.split('/');
      const lastPart = parts[parts.length - 1]; // Get the filename part
      // Remove timestamp and random string prefix (format: timestamp_randomstring_originalname.ext)
      const match = lastPart.match(/^\d+_[a-f0-9]+_(.+)$/);
      if (match) {
        return match[1]; // Return the original filename part
      }
      return lastPart; // Fallback to the last part of the path
    }
    
    // Extract from fileName if it's an S3 key
    if (fileData.fileName) {
      const parts = fileData.fileName.split('/');
      const lastPart = parts[parts.length - 1];
      const match = lastPart.match(/^\d+_[a-f0-9]+_(.+)$/);
      if (match) {
        return match[1];
      }
      return lastPart;
    }
    
    return fileData.name || 'Unknown file';
  };

  // Handle file deletion (only removes local reference, keeps file in S3)
  const handleDelete = async () => {
    try {
      if (localDeleteOnly) {
        // Local-only delete: just clear the state without any API calls
        const result = removeFileReferenceLocal(fieldName);
        if (result.success && onChange) {
          onChange(fieldName, null);
        }
        // Reset the file input to allow re-uploading the same file
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Original behavior: remove reference via API (but keep file in S3)
      const result = await removeFileReference(fieldName);
      if (result.success && onChange) {
        onChange(fieldName, null);
      }
      // Reset the file input to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Delete error:', error);
      // Fallback: just remove local reference
      const fallbackResult = removeFileReferenceLocal(fieldName);
      if (fallbackResult.success && onChange) {
        onChange(fieldName, null);
      }
      // Reset the file input even on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle file view/download
  const handleView = async () => {
    try {
      let result;
      
      // Check if this is an actively uploaded file or an initial file
      if (file) {
        // This is an actively uploaded file, use the existing method
        result = await getFileUrl(fieldName);
      } else if (initialFile && (initialFile.key || initialFile.fileName)) {
        // This is a file loaded from database, use the new method
        const fileKey = initialFile.key || initialFile.fileName;
        result = await getFileUrlByKey(fileKey);
      } else {
        console.error('No file key or fileName found for viewing');
        return;
      }

      if (result.success && result.data && result.data.url) {
        // Open the file in a new tab
        window.open(result.data.url, '_blank');
      } else {
        console.error('Failed to get file URL:', result.error);
        // You might want to show an error message to the user here
        alert('Failed to open file: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('View error:', error);
      alert('Failed to open file: ' + error.message);
    }
  };

  // Open file dialog
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
        {label}
        {required && <span style={{ color: 'red', fontWeight: 'bold' }}>*</span>}
      </Typography>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {/* Upload area */}
      {!displayFile && !isUploading && (
        <Paper
          sx={{
            border: dragOver ? '2px dashed #667eea' : '2px dashed #ddd',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#f5f5f5' : (dragOver ? 'rgba(102, 126, 234, 0.04)' : 'white'),
            opacity: disabled ? 0.6 : 1,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: disabled ? '#ddd' : '#667eea',
              backgroundColor: disabled ? '#f5f5f5' : 'rgba(102, 126, 234, 0.02)'
            }
          }}
          onDrop={disabled ? undefined : handleDrop}
          onDragOver={disabled ? undefined : handleDragOver}
          onDragLeave={disabled ? undefined : handleDragLeave}
          onClick={disabled ? undefined : openFileDialog}
        >
          <UploadIcon sx={{ fontSize: 48, color: disabled ? '#999' : '#667eea', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: disabled ? '#999' : '#667eea' }}>
            {disabled ? 'Upload Disabled' : 'Choose File or Drag & Drop'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Accepted formats: {accept}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Maximum size: {maxSize}MB
          </Typography>
        </Paper>
      )}

      {/* Upload progress */}
      {isUploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Uploading... {progress}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{
              borderRadius: 1,
              backgroundColor: '#f0f0f0',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }
            }}
          />
        </Box>
      )}

      {/* Upload error */}
      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Uploaded file display */}
      {displayFile && !isUploading && (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: '#333',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: 1.3
                  }}
                >
                  {getDisplayFileName(displayFile)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {displayFile.fileSize ? `${Math.round(displayFile.fileSize / 1024)} KB` : ''} • 
                  Uploaded: {displayFile.uploadDate ? new Date(displayFile.uploadDate).toLocaleDateString() : 'Recently'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="View File">
                  <IconButton 
                    size="small" 
                    onClick={handleView}
                    sx={{ color: '#667eea' }}
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title={disabled ? "Delete disabled for read-only section" : "Delete File"}>
                  <span>
                    <IconButton 
                      size="small" 
                      onClick={handleDelete}
                      disabled={disabled}
                      sx={{ 
                        color: disabled ? '#ccc' : '#f44336',
                        '&.Mui-disabled': {
                          color: '#ccc'
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Manual upload button (alternative to drag & drop) */}
      {!displayFile && !isUploading && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            component="span"
            startIcon={<UploadIcon />}
            onClick={openFileDialog}
            disabled={disabled}
            sx={{
              borderColor: disabled ? '#ccc' : '#667eea',
              color: disabled ? '#ccc' : '#667eea',
              '&:hover': disabled ? {} : {
                borderColor: '#764ba2',
                backgroundColor: 'rgba(102, 126, 234, 0.04)',
              },
              '&.Mui-disabled': {
                borderColor: '#ccc',
                color: '#ccc'
              }
            }}
          >
            Choose File
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FileUploadComponent;
